import { useCallback, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { ErrorModal } from "@dexteel/mesf-core";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { DeleteConfirmModal } from "@/controls/modals/DeleteConfirmModal";
import type { PassChangeReason } from "../models/PassChangeReason";
import { PASS_CHANGE_REASON_INITIAL_VALUES } from "../models/PassChangeReason";
import { usePassChangeReasons } from "../hooks/usePassChangeReasons";
import {
	useUpsertPassChangeReason,
	useRemovePassChangeReason,
} from "../hooks/usePassChangeReasonMutation";
import { PassChangeReasonsTable } from "./PassChangeReasonsTable";
import { PassChangeReasonDetailViewer } from "./PassChangeReasonDetailViewer";
import { PassChangeReasonsToolbar } from "./PassChangeReasonsToolbar";
import { SHOW_ONLY_ACTIVES_DEFAULT } from "@/controls/filters/ShowOnlyActivesCheckbox";
import { urlParam, useUrlState, useUrlText } from "@/shared/hooks/useUrlState";
import { useUrlSelection } from "@/shared/hooks/useUrlSelection";

// Default layout percentages (left: 55%, right: 45%)
const DEFAULT_LAYOUT = { left: 55, right: 45 };

const URL_SCHEMA = {
	search: urlParam.string(),
	onlyActives: urlParam.bool(SHOW_ONLY_ACTIVES_DEFAULT),
	passChangeReasonId: urlParam.int(),
};

export const PassChangeReasonsManager = () => {
	// Filters and the selected record, all in the query string.
	const [
		{ search, onlyActives, passChangeReasonId },
		setFilters,
		{ dropValues, resetValues, isDefault },
	] = useUrlState(URL_SCHEMA);
	// The box types locally and the URL follows once typing stops.
	const [searchText, setSearchText] = useUrlText(search ?? "", (value) =>
		setFilters({ search: value || null }),
	);
	const [error, setError] = useState("");
	const [notification, setNotification] = useState("");
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [reasonToDelete, setReasonToDelete] = useState<PassChangeReason | null>(
		null,
	);

	// Resizable panels layout persistence
	const { defaultLayout, onLayoutChange } = useDefaultLayout({
		id: "pass-change-reasons-manager-v1",
		panelIds: ["left", "right"],
		storage: localStorage,
	});

	// Data fetching
	const {
		data: reasons = [],
		isLoading,
		error: queryError,
	} = usePassChangeReasons();

	// The id travels and the record is resolved from the list. A record being added has
	// no id yet, so it stays out of the URL.
	const selection = useUrlSelection<PassChangeReason>({
		id: passChangeReasonId,
		setId: (value) => setFilters({ passChangeReasonId: value }),
		dropId: () => dropValues("passChangeReasonId"),
		rows: reasons,
		ready: !isLoading,
		getId: (record) => record.Id,
	});
	const selectedReason = selection.current;

	// Mutations
	const { upsertPassChangeReason, isUpserting } = useUpsertPassChangeReason({
		onSuccess: (data) => {
			const isNew = selection.isNew;
			setNotification(
				isNew
					? "Pass change reason created successfully"
					: "Pass change reason updated successfully",
			);
			if (data) {
				selection.adopt(data);
			}
		},
		onError: (err) => setError(err.message),
	});

	const { removePassChangeReason, isRemoving } = useRemovePassChangeReason({
		onSuccess: () => {
			setNotification("Pass change reason deleted successfully");
			selection.clear();
			setDeleteModalOpen(false);
			setReasonToDelete(null);
		},
		onError: (err) => setError(err.message),
	});

	// Handlers
	const handleRowSelected = useCallback(
		(reason: PassChangeReason | null) => {
			if (reason) selection.select(reason);
			else selection.clear();
		},
		[selection],
	);

	const handleDelete = useCallback((reason: PassChangeReason) => {
		setReasonToDelete(reason);
		setDeleteModalOpen(true);
	}, []);

	const handleConfirmDelete = useCallback(() => {
		if (reasonToDelete?.Id) {
			removePassChangeReason(reasonToDelete.Id);
		}
	}, [reasonToDelete, removePassChangeReason]);

	const handleCancelDelete = useCallback(() => {
		setDeleteModalOpen(false);
		setReasonToDelete(null);
	}, []);

	const handleAdd = useCallback(() => {
		selection.startNew({ ...PASS_CHANGE_REASON_INITIAL_VALUES });
	}, [selection]);

	const handleSave = useCallback(
		(reason: PassChangeReason) => {
			upsertPassChangeReason(reason);
		},
		[upsertPassChangeReason],
	);

	const handleClose = useCallback(() => {
		selection.clear();
	}, [selection]);

	// Filter reasons by search
	const filteredReasons = reasons.filter(
		(r) =>
			(!onlyActives || r.Active) &&
			(!searchText ||
				r.Name.toLowerCase().includes(searchText.toLowerCase()) ||
				(r.Description &&
					r.Description.toLowerCase().includes(searchText.toLowerCase()))),
	);

	if (queryError) {
		return <ErrorModal error={queryError.message} onHide={() => {}} />;
	}

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 130px)",
				display: "flex",
				flexDirection: "column",
				gap: 1,
				p: 1,
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Pass Change Reasons
			</Typography>

			<Paper elevation={3}>
				<PassChangeReasonsToolbar
					onReset={() => resetValues(["search", "onlyActives"])}
					canReset={!isDefault("search", "onlyActives")}
					search={searchText}
					onSearchChange={setSearchText}
					onlyActives={onlyActives}
					onOnlyActivesChange={(value) => setFilters({ onlyActives: value })}
					onAdd={handleAdd}
				/>
			</Paper>

			<Group
				id="pass-change-reasons-manager-group"
				orientation="horizontal"
				defaultLayout={defaultLayout ?? DEFAULT_LAYOUT}
				onLayoutChange={onLayoutChange}
				style={{ flex: 1, width: "100%" }}
			>
				<Panel id="left" minSize={25} style={{ paddingBottom: 5 }}>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							height: "100%",
							pr: 1,
						}}
					>
						<Paper
							elevation={3}
							sx={{
								flex: 1,
								width: "100%",
								overflow: "hidden",
							}}
						>
							{isLoading ? (
								<CenteredLazyLoading />
							) : (
								<PassChangeReasonsTable
									reasons={filteredReasons}
									selectedReason={selectedReason?.Id ? selectedReason : null}
									onRowSelected={handleRowSelected}
									onDelete={handleDelete}
								/>
							)}
						</Paper>
					</Box>
				</Panel>

				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 8,
							height: 48,
							borderRadius: 1,
							backgroundColor: "#e0e0e0",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							gap: "3px",
							transition: "background-color 0.2s",
							"&:hover": {
								backgroundColor: "primary.main",
								"& .grip-dot": {
									backgroundColor: "#fff",
								},
							},
						}}
					>
						{[...Array(5)].map((_, i) => (
							<Box
								key={`grip-dot-${i}`}
								className="grip-dot"
								sx={{
									width: 4,
									height: 4,
									borderRadius: "50%",
									backgroundColor: "#9e9e9e",
									transition: "background-color 0.2s",
								}}
							/>
						))}
					</Box>
				</Separator>

				<Panel id="right" minSize={30} style={{ paddingBottom: 5 }}>
					<Box sx={{ pl: 1, height: "100%" }}>
						<Paper
							elevation={3}
							sx={{
								height: "100%",
								overflowY: "auto",
								overflowX: "hidden",
							}}
						>
							<PassChangeReasonDetailViewer
								reason={selectedReason}
								existingReasons={reasons}
								isSaving={isUpserting || isRemoving}
								onSave={handleSave}
								onClose={handleClose}
							/>
						</Paper>
					</Box>
				</Panel>
			</Group>

			<DeleteConfirmModal
				open={deleteModalOpen}
				title="DELETE PASS CHANGE REASON"
				itemName={reasonToDelete?.Name ?? ""}
				isDeleting={isRemoving}
				onConfirm={handleConfirmDelete}
				onCancel={handleCancelDelete}
			/>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
